import { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import type { Property } from '../../types';

// Free tier Mapbox token - for production, use environment variable
const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN || 'pk.eyJ1IjoiaW5zcGVjdGFpIiwiYSI6ImNtNXJxb3g4MTBxYTcyanNjdThiY2g4NjYifQ.placeholder';

interface PropertyMapProps {
  properties: Property[];
  onPropertyClick?: (property: Property) => void;
}

// Geocode an address to coordinates using Mapbox Geocoding API
async function geocodeAddress(address: string): Promise<[number, number] | null> {
  try {
    const encodedAddress = encodeURIComponent(address);
    const response = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodedAddress}.json?access_token=${MAPBOX_TOKEN}&limit=1`
    );
    const data = await response.json();
    if (data.features && data.features.length > 0) {
      return data.features[0].center as [number, number];
    }
    return null;
  } catch {
    return null;
  }
}

export function PropertyMap({ properties, onPropertyClick }: PropertyMapProps): JSX.Element {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);

  useEffect(() => {
    if (!mapContainerRef.current) return;

    // Check if token is valid
    if (!MAPBOX_TOKEN || MAPBOX_TOKEN.includes('placeholder')) {
      setError('Mapbox token not configured. Add VITE_MAPBOX_TOKEN to environment variables.');
      setIsLoading(false);
      return;
    }

    mapboxgl.accessToken = MAPBOX_TOKEN;

    // Initialize map
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: [151.2093, -33.8688], // Sydney default
      zoom: 10,
    });

    mapRef.current = map;

    map.on('load', async () => {
      setIsLoading(false);

      // Add navigation controls
      map.addControl(new mapboxgl.NavigationControl(), 'top-right');

      // Geocode and add markers for properties
      const bounds = new mapboxgl.LngLatBounds();
      let hasValidCoordinates = false;

      for (const property of properties) {
        const address = `${property.address.line1}, ${property.address.city}, ${property.address.state} ${property.address.postcode}, ${property.address.country}`;
        const coordinates = await geocodeAddress(address);

        if (coordinates) {
          hasValidCoordinates = true;
          bounds.extend(coordinates);

          // Create custom marker element
          const markerEl = document.createElement('div');
          markerEl.className = 'property-marker';
          markerEl.innerHTML = `
            <div style="
              width: 40px;
              height: 40px;
              background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              color: white;
              font-weight: bold;
              font-size: 14px;
              box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
              cursor: pointer;
              transition: transform 0.2s;
            ">
              ${property.name.substring(0, 2).toUpperCase()}
            </div>
          `;

          markerEl.addEventListener('mouseenter', () => {
            markerEl.style.transform = 'scale(1.1)';
          });
          markerEl.addEventListener('mouseleave', () => {
            markerEl.style.transform = 'scale(1)';
          });

          // Create popup
          const popup = new mapboxgl.Popup({
            offset: 25,
            closeButton: false,
            className: 'property-popup',
          }).setHTML(`
            <div style="padding: 8px; min-width: 180px;">
              <h4 style="margin: 0 0 4px; font-weight: 600; color: #1f2937;">${property.name}</h4>
              <p style="margin: 0; font-size: 12px; color: #6b7280;">${property.address.line1}</p>
              <p style="margin: 0; font-size: 12px; color: #6b7280;">${property.address.city}, ${property.address.state}</p>
            </div>
          `);

          const marker = new mapboxgl.Marker({ element: markerEl })
            .setLngLat(coordinates)
            .setPopup(popup)
            .addTo(map);

          markerEl.addEventListener('click', () => {
            onPropertyClick?.(property);
          });

          markersRef.current.push(marker);
        }
      }

      // Fit map to show all markers
      if (hasValidCoordinates && properties.length > 0) {
        map.fitBounds(bounds, {
          padding: { top: 50, bottom: 50, left: 50, right: 50 },
          maxZoom: 15,
        });
      }
    });

    map.on('error', (e) => {
      console.error('Mapbox error:', e);
      setError('Failed to load map. Please check your Mapbox configuration.');
      setIsLoading(false);
    });

    return () => {
      // Cleanup markers
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current = [];
      map.remove();
    };
  }, [properties, onPropertyClick]);

  if (error) {
    return (
      <div className="flex h-full items-center justify-center rounded-xl bg-gradient-to-br from-blue-50 to-blue-100 p-8 dark:from-blue-950/30 dark:to-blue-900/20">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
            <svg className="h-8 w-8 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{error}</p>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            To enable maps, add your Mapbox token to the environment configuration.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-full w-full overflow-hidden rounded-xl">
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80 dark:bg-gray-900/80">
          <div className="flex flex-col items-center gap-3">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            <p className="text-sm text-muted-foreground">Loading map...</p>
          </div>
        </div>
      )}
      <div ref={mapContainerRef} className="h-full w-full" />
    </div>
  );
}

